import { useState } from 'react';
import { Heart, Target, TrendingUp, Sparkles, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { reportError } from '@/lib/error-reporting';

interface OnboardingWelcomeProps {
  onComplete: () => void;
}

const steps = [
  {
    icon: Heart,
    title: 'Discover Your Values',
    description: 'Uncover what truly matters to guide your career decisions.',
  },
  {
    icon: Target,
    title: 'Set Meaningful Goals',
    description: 'AI-guided goals aligned with your values across Education, Exposure, and Experience.',
  },
  {
    icon: TrendingUp,
    title: 'Build Momentum',
    description: 'Daily habits, coaching insights, and a personalized development plan.',
  },
];

export const OnboardingWelcome = ({ onComplete }: OnboardingWelcomeProps) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const handleOptIn = async (enableCoaching: boolean) => {
    if (!user) return;
    setSaving(true);
    try {
      await supabase
        .from('profiles')
        .update({
          enhanced_coaching_prompted: true,
          ...(enableCoaching && { enhanced_coaching_enabled: true }),
        })
        .eq('user_id', user.id);

      onComplete();
    } catch (err) {
      reportError(err, 'auth', { action: 'onboarding-welcome' });
      // Still proceed even on error — don't block onboarding
      onComplete();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh] animate-fade-in">
      <Card className="max-w-lg w-full card-elevated">
        <CardContent className="pt-8 pb-6 px-6 space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mb-2">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-display font-bold">Welcome to MomentumEQ</h1>
            <p className="text-muted-foreground">
              Your values-driven career development journey starts here.
            </p>
          </div>

          {/* Journey Steps */}
          <div className="space-y-4">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
                  <step.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Coaching Opt-in */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Enhanced AI Coaching</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Enable personalized coaching that remembers your history and provides
                  contextual recommendations. Your data stays private and you can disable
                  this anytime in Settings.
                </p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => handleOptIn(true)}
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Setting up...' : 'Enable Enhanced Coaching & Start'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleOptIn(false)}
              disabled={saving}
              className="w-full text-muted-foreground"
            >
              Start with Core Features
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Enhanced coaching uses your history for deeper personalization.
            You can enable it anytime in Settings.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
